<template>
  <div class="workspace-filter" @focusout="handleFocusOut">
    <input
      :id="inputId"
      ref="inputRef"
      v-model="query"
      type="text"
      class="form-input workspace-filter__input"
      role="combobox"
      autocomplete="off"
      aria-autocomplete="list"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :aria-activedescendant="highlightedId || undefined"
      :aria-labelledby="labelledBy || undefined"
      :aria-label="labelledBy ? undefined : 'Filter runs by workspace'"
      :placeholder="placeholder"
      @focus="openList"
      @input="handleInput"
      @keydown.down.prevent="onArrowDown"
      @keydown.up.prevent="onArrowUp"
      @keydown.home="onHome"
      @keydown.end="onEnd"
      @keydown.enter.prevent="onEnter"
      @keydown.escape.prevent="closeList"
    />
    <div v-if="isOpen" class="workspace-filter__panel">
      <div
        :id="listboxId"
        class="workspace-filter__listbox"
        role="listbox"
        aria-label="Workspaces"
      >
        <button
          v-if="showAllOption"
          :id="optionDomId('all')"
          type="button"
          role="option"
          class="workspace-filter__option workspace-filter__option--all"
          :class="{ 'workspace-filter__option--active': highlightedId === optionDomId('all') }"
          :aria-selected="selectedId === ''"
          data-testid="workspace-option-all"
          @mousedown.prevent
          @click="selectAll"
          @keydown.enter.prevent="selectAll"
          @keydown.space.prevent="selectAll"
        >
          All workspaces
        </button>

        <div
          v-if="namedResults.length"
          role="group"
          aria-label="Named workspaces"
          class="workspace-filter__group"
        >
          <p class="workspace-filter__group-label" aria-hidden="true">Named workspaces</p>
          <button
            v-for="option in namedResults"
            :key="`named-${option.workspaceId}`"
            :id="optionDomId(`named-${option.workspaceId}`)"
            type="button"
            role="option"
            class="workspace-filter__option workspace-filter__option--named"
            :class="{
              'workspace-filter__option--selected': option.workspaceId === selectedId,
              'workspace-filter__option--active': highlightedId === optionDomId(`named-${option.workspaceId}`),
            }"
            :aria-selected="option.workspaceId === selectedId"
            data-testid="workspace-option-named"
            @mousedown.prevent
            @click="selectOption(option)"
            @keydown.enter.prevent="selectOption(option)"
            @keydown.space.prevent="selectOption(option)"
          >
            <span class="workspace-filter__option-name">{{ option.name }}</span>
            <span class="workspace-filter__option-id">{{ option.workspaceId }}</span>
          </button>
        </div>

        <div
          v-if="rawResults.length"
          role="group"
          aria-label="Workspace IDs"
          class="workspace-filter__group"
        >
          <p class="workspace-filter__group-label" aria-hidden="true">Workspace IDs</p>
          <button
            v-for="option in rawResults"
            :key="`raw-${option.workspaceId}`"
            :id="optionDomId(`raw-${option.workspaceId}`)"
            type="button"
            role="option"
            class="workspace-filter__option workspace-filter__option--raw"
            :class="{
              'workspace-filter__option--selected': option.workspaceId === selectedId,
              'workspace-filter__option--active': highlightedId === optionDomId(`raw-${option.workspaceId}`),
            }"
            :aria-selected="option.workspaceId === selectedId"
            data-testid="workspace-option-raw"
            @mousedown.prevent
            @click="selectOption(option)"
            @keydown.enter.prevent="selectOption(option)"
            @keydown.space.prevent="selectOption(option)"
          >
            <span class="workspace-filter__option-id">{{ option.workspaceId }}</span>
          </button>
        </div>
      </div>

      <p v-if="isEmptyState" class="workspace-filter__empty">No matching workspaces.</p>
    </div>
  </div>
</template>

<script setup>
// Plain-JS SFC to match every other console component. The app's webpack build
// (`npm run build`) has no TypeScript loader, so a `lang="ts"` SFC fails to
// build; the { workspaceId, name } workspace shape is documented here and
// asserted by the component spec.
import { computed, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: String, required: true },
  workspaces: { type: Array, required: true },
  placeholder: { type: String, default: 'Search workspaces by name or ID' },
  labelledBy: { type: String, default: undefined },
})

const emit = defineEmits(['update:modelValue'])

// Fixed DOM ids for the combobox/listbox pairing and per-option
// aria-activedescendant targets. A single filter renders per Runs page, so
// stable ids are safe and match the existing listbox id contract.
const inputId = 'runs-workspace-filter-input'
const listboxId = 'runs-workspace-filter-listbox'

function optionDomId(suffix) {
  return `runs-workspace-filter-option-${suffix}`
}

const inputRef = ref(null)
const query = ref('')
const isOpen = ref(false)
// Index into the flattened, render-ordered option list. -1 = nothing
// highlighted (the keyboard user has not arrowed into the list yet).
const highlightedIndex = ref(-1)

const selectedId = computed(() => props.modelValue)

const selectedLabel = computed(() => {
  if (!props.modelValue) return ''
  const match = props.workspaces.find((w) => w.workspaceId === props.modelValue)
  if (!match) return props.modelValue
  return isNamedWorkspace(match) ? match.name : match.workspaceId
})

watch(
  () => props.modelValue,
  () => {
    if (!isOpen.value) {
      query.value = selectedLabel.value
    }
  },
  { immediate: true },
)

watch(
  () => props.workspaces,
  () => {
    if (!isOpen.value) {
      query.value = selectedLabel.value
    }
  },
)

function isNamedWorkspace(option) {
  const name = option.name?.trim() ?? ''
  return name !== '' && name !== option.workspaceId
}

function matches(option, term) {
  if (!term) return true
  const needle = term.toLowerCase()
  const nameHit = option.name?.toLowerCase().includes(needle) ?? false
  const idHit = option.workspaceId.toLowerCase().includes(needle)
  return nameHit || idHit
}

// Typing always filters by the literal typed text. We intentionally do NOT
// collapse the term to '' when it equals the current selection label —
// otherwise typing the exact name of the selected workspace would surface the
// full list instead of narrowing to that workspace (review finding 6).
const filterTerm = computed(() => query.value.trim())

const namedResults = computed(() =>
  props.workspaces
    .filter(isNamedWorkspace)
    .filter((option) => matches(option, filterTerm.value))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name)),
)

const rawResults = computed(() =>
  props.workspaces
    .filter((option) => !isNamedWorkspace(option))
    .filter((option) => matches(option, filterTerm.value))
    .slice()
    .sort((a, b) => a.workspaceId.localeCompare(b.workspaceId)),
)

const showAllOption = computed(() => filterTerm.value === '')

const isEmptyState = computed(
  () => !namedResults.value.length && !rawResults.value.length && !showAllOption.value,
)

// Flattened option list in the exact order options render, used for keyboard
// navigation and Enter-to-commit. `option: null` marks the "All workspaces"
// reset entry.
const flatOptions = computed(() => {
  const items = []
  if (showAllOption.value) {
    items.push({ id: optionDomId('all'), option: null })
  }
  for (const option of namedResults.value) {
    items.push({ id: optionDomId(`named-${option.workspaceId}`), option })
  }
  for (const option of rawResults.value) {
    items.push({ id: optionDomId(`raw-${option.workspaceId}`), option })
  }
  return items
})

const highlightedId = computed(() => {
  const idx = highlightedIndex.value
  if (idx < 0 || idx >= flatOptions.value.length) return null
  return flatOptions.value[idx].id
})

// Keep the highlight index valid as the visible option set changes while the
// operator types.
watch(flatOptions, (options) => {
  if (highlightedIndex.value >= options.length) {
    highlightedIndex.value = options.length - 1
  }
})

function openList() {
  isOpen.value = true
  if (query.value === selectedLabel.value) {
    query.value = ''
  }
}

function closeList() {
  isOpen.value = false
  highlightedIndex.value = -1
  query.value = selectedLabel.value
  // Escape keeps focus on the combobox input (review finding 7) so keyboard
  // operators are not ejected out of the control; we deliberately do not blur.
}

function handleInput() {
  isOpen.value = true
  highlightedIndex.value = -1
}

function handleFocusOut(event) {
  const nextTarget = event.relatedTarget
  const container = event.currentTarget
  if (container && nextTarget && container.contains(nextTarget)) {
    return
  }
  isOpen.value = false
  highlightedIndex.value = -1
  query.value = selectedLabel.value
}

function moveHighlight(delta) {
  if (!isOpen.value) {
    openList()
  }
  const count = flatOptions.value.length
  if (count === 0) {
    highlightedIndex.value = -1
    return
  }
  let next = highlightedIndex.value + delta
  if (next < 0) next = count - 1
  else if (next >= count) next = 0
  highlightedIndex.value = next
}

function onArrowDown() {
  moveHighlight(1)
}

function onArrowUp() {
  moveHighlight(-1)
}

function onHome(event) {
  if (!isOpen.value || flatOptions.value.length === 0) return
  event.preventDefault()
  highlightedIndex.value = 0
}

function onEnd(event) {
  if (!isOpen.value || flatOptions.value.length === 0) return
  event.preventDefault()
  highlightedIndex.value = flatOptions.value.length - 1
}

// Enter on the input commits the currently highlighted option (review
// findings 1 and 2). With nothing highlighted it is a no-op.
function onEnter() {
  if (!isOpen.value) return
  const idx = highlightedIndex.value
  if (idx < 0 || idx >= flatOptions.value.length) return
  const target = flatOptions.value[idx]
  if (target.option) {
    selectOption(target.option)
  } else {
    selectAll()
  }
}

function selectAll() {
  emit('update:modelValue', '')
  query.value = ''
  isOpen.value = false
  highlightedIndex.value = -1
}

function selectOption(option) {
  emit('update:modelValue', option.workspaceId)
  query.value = isNamedWorkspace(option) ? option.name : option.workspaceId
  isOpen.value = false
  highlightedIndex.value = -1
}
</script>

<style scoped>
/*
 * Only single-component layout stays scoped here. The panel / option / group
 * / empty-state visuals live in the centralized console stylesheet
 * (src/styles/console.css) per .claude/rules/frontend.md.
 */
.workspace-filter {
  position: relative;
  width: 100%;
}

.workspace-filter__input {
  width: 100%;
}
</style>
